<template>
  <div :class="$style.wrapper">
    <!-- Header -->
    <div :class="$style.topBar">
      <div :class="$style.searchBox">
        <input
          v-model="q"
          @input="onSearch"
          type="text"
          :placeholder="searchPlaceholder"
        />
        <img src="@/assets/img/market/search.svg" />
      </div>

      <!-- FILTER BUTTON WITH ACTIVE BORDER -->
      <button
        v-if="shouldShowFilters"
        :class="[$style.iconBtn, { [$style.filterActive]: hasFilters }]"
        @click="filtersOpen = true"
      >
        <img src="@/assets/img/market/filters.svg" />
      </button>

      <button v-if="shouldShowSort" :class="$style.iconBtn" @click="openSort">
        <img src="@/assets/img/market/sort.svg" />
      </button>

      <button
        v-if="this.$store.state.user.type === 'reseller'"
        :class="[$style.iconBtn, isBidDisabled && $style.disabled]"
        type="button"
        :disabled="isBidDisabled"
        @click="openBid"
      >
        <img src="@/assets/img/market/cart.svg" />
      </button>
    </div>

    <!-- Categories -->
    <div :class="$style.categoriesWrapper">
      <div
        v-if="!categoriesLoading && canScrollLeft"
        :class="$style.categoriesFadeLeft"
        aria-hidden="true"
      />
      <div
        ref="categoriesContainer"
        :class="[
          $style.categories,
          categoriesDrag.active && $style.categoriesGrabbing,
        ]"
        @scroll="updateScrollButtons"
        @mousedown="onCategoriesMouseDown"
      >
        <template v-if="categoriesLoading">
          <div
            v-for="n in 6"
            :key="`category-skeleton-${n}`"
            :class="[$style.categoryBtn, $style.categorySkeleton]"
          >
            <div class="skeleton" :class="$style.categorySkeletonBar"></div>
          </div>
        </template>
        <template v-else>
          <button
            v-for="cat in categories"
            :key="cat.name"
            :data-category="cat.name"
            :class="[
              $style.categoryBtn,
              { [$style.active]: category === cat.name },
            ]"
            @click.prevent="onCategoryClick(cat.name)"
          >
            {{ cat.label }}
          </button>
        </template>
      </div>
      <div
        v-if="!categoriesLoading && canScrollRight"
        :class="$style.categoriesFadeRight"
        aria-hidden="true"
      />
    </div>

    <!-- Content -->
    <div v-if="isBrandsMode" :class="$style.brandListWrapper">
      <div v-if="brandLoading" :class="$style.brandList">
        <div
          v-for="n in 6"
          :key="`brand-skeleton-${n}`"
          :class="$style.brandCard"
        >
          <div :class="['skeleton', $style.brandTitleSkeleton]"></div>
          <div :class="['skeleton', $style.brandCountSkeleton]"></div>
        </div>
      </div>
      <div v-else>
        <div v-if="brandList.length" :class="$style.brandList">
          <button
            v-for="brand in brandList"
            :key="brand.name"
            :class="$style.brandCard"
            type="button"
            @click="openBrand(brand)"
          >
            <div>
              <div :class="$style.brandName">{{ brand.name }}</div>
              <div :class="$style.brandCount">
                {{ pluralizeProducts(brand.count) }}
              </div>
            </div>
            <span :class="$style.brandChevron">
              <img src="@/assets/img/account/chev.svg" alt="" />
            </span>
          </button>
        </div>
        <div v-else :class="$style.empty">Бренды не найдены</div>
      </div>
    </div>
    <div v-else-if="isSellersMode" :class="$style.brandListWrapper">
      <div v-if="!sellersLoading && sellersStats" :class="$style.sellersStats">
        <div :class="$style.statCard">
          <div :class="$style.statValue">{{ sellersStats.sellers_total }}</div>
          <div :class="$style.statLabel">
            {{ pluralizeSellers(sellersStats.sellers_total) }}
          </div>
        </div>
        <div :class="$style.statCard">
          <div :class="$style.statValue">
            {{ sellersStats.resellers_total }}
          </div>
          <div :class="$style.statLabel">
            {{ pluralizeResellers(sellersStats.resellers_total) }}
          </div>
        </div>
      </div>

      <div v-if="sellersLoading" :class="$style.brandList">
        <div
          v-for="n in 6"
          :key="`seller-skeleton-${n}`"
          :class="$style.brandCard"
        >
          <div :class="['skeleton', $style.sellerAvatarSkeleton]"></div>
          <div :class="$style.sellerInfoSkeleton">
            <div :class="['skeleton', $style.brandTitleSkeleton]"></div>
            <div :class="['skeleton', $style.brandCountSkeleton]"></div>
          </div>
        </div>
      </div>
      <div v-else>
        <div v-if="sellersList.length" :class="$style.brandList">
          <button
            v-for="seller in sellersList"
            :key="seller.id"
            :class="$style.brandCard"
            type="button"
            @click="openSeller(seller)"
          >
            <div :class="$style.sellerCardContent">
              <div
                :class="[
                  $style.sellerAvatar,
                  !seller.avatar && $style.sellerAvatarPlaceholder,
                ]"
              >
                <img v-if="seller.avatar" :src="seller.avatar" alt="" />
                <span v-else>{{ seller.name.charAt(0).toUpperCase() }}</span>
              </div>
              <div>
                <div :class="$style.brandName">{{ seller.name }}</div>
                <div :class="$style.brandCount">
                  {{ pluralizeProducts(seller.count) }}
                </div>
              </div>
            </div>
            <div v-if="seller.rating_count > 0" :class="$style.sellerRating">
              <span :class="$style.ratingValue">{{ seller.rating }}</span>
              <img
                src="@/assets/img/account/star.svg"
                alt=""
                :class="$style.ratingStar"
              />
            </div>
            <span :class="$style.brandChevron">
              <img src="@/assets/img/account/chev.svg" alt="" />
            </span>
          </button>
        </div>
        <div v-else :class="$style.empty">Селлеры не найдены</div>
      </div>
    </div>
    <div v-else>
      <div
        v-if="isLoading && !isBrandsMode && !isSellersMode"
        :class="$style.grid"
      >
        <div
          v-for="n in 6"
          :key="`market-skeleton-${n}`"
          :class="$style.cardSkeleton"
        >
          <div :class="[$style.skeletonImage, 'skeleton']"></div>
          <div :class="[$style.skeletonLine, 'skeleton']"></div>
          <div :class="[$style.skeletonLineShort, 'skeleton']"></div>
        </div>
      </div>
      <div v-else-if="!isBrandsMode && !isSellersMode">
        <div v-if="!items || !items.length" :class="$style.emptyState">
          <p>Товаров не найдено</p>
          <span>Попробуйте изменить параметры поиска или фильтры.</span>
        </div>
        <div v-else :class="$style.grid">
          <Card
            v-for="item in items"
            :key="item.model_id"
            :item="item"
            @click="openModel(item.model_id)"
          />
          <div v-if="isLoadingMore" :class="$style.loadingMore">
            <div :class="$style.loadingSpinner"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sort Modal -->
    <div
      v-if="sortModal"
      :class="[$style.sortBg, { [$style.fadeOut]: sortClosing }]"
      @click="closeSort"
    >
      <div
        :class="[$style.sortModal, { [$style.slideDown]: sortClosing }]"
        @click.stop
      >
        <div :class="$style.sortHeader"></div>
        <div :class="$style.sortTitle">Сортировка</div>

        <div :class="$style.sortList">
          <div
            v-for="s in currentSortOptions"
            :key="s.value"
            :class="$style.sortItem"
            @click="applySort(s.value)"
          >
            <span>{{ s.label }}</span>
            <div
              :class="[
                $style.radio,
                { [$style.radioActive]: sort === s.value },
              ]"
            >
              <img
                v-if="sort === s.value"
                src="@/assets/img/market/check.svg"
                alt=""
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Fullscreen Modal -->
    <FiltersModal
      v-if="filtersOpen"
      :filters="filters"
      @close="filtersOpen = false"
      @apply="applyFilters"
      @open-brands="openBrandsSection"
    />
  </div>
</template>

<script>
import Card from "@/components/Card.vue"
import FiltersModal from "@/components/FiltersModal.vue"
import { pluralizeProducts, pluralizeWord } from "@/utils/pluralize"

const CATALOG_CATEGORY = "__catalog__"
const BRANDS_CATEGORY = "__brands__"
const SELLERS_CATEGORY = "__sellers__"

/** Порядок категорий товаров: Мужские, Женские, Унисекс, Детские */
const PRODUCT_CATEGORY_ORDER = ["MALE", "FEMALE", "UNISEX", "TEENAGER"]

export default {
  name: "Market",
  components: { Card, FiltersModal },

  data() {
    return {
      q: null,
      limit: 20,
      offset: 0,
      isLoading: true,
      isLoadingMore: false,
      hasMore: true,
      brandLoading: false,

      sort: "random",
      sortModal: false,
      sortClosing: false,

      filtersOpen: false,

      filters: {
        brand: [],
        color: null,
        size: null,
        size_type: null,
        custom: null,
      },

      category: "",
      categories: [],
      categoriesLoading: true,
      canScrollLeft: false,
      canScrollRight: false,
      isTransitioning: false,
      brandList: [],
      brandLimit: 100,
      sellersList: [],
      sellersLimit: 100,
      sellersLoading: false,
      sellersStats: null,
      defaultCategoryName: "",

      sortOptions: [
        { label: "По цене ↑", value: "price_asc" },
        { label: "По цене ↓", value: "price_desc" },
        { label: "Сначала новые", value: "date_desc" },
        { label: "Сначала старые", value: "date_asc" },
        { label: "Популярные", value: "random" },
      ],
      sellersSortOptions: [
        { label: "Рейтинг: выше", value: "rating_desc" },
        { label: "Рейтинг: ниже", value: "rating_asc" },
      ],

      searchTimer: null,

      categoriesDrag: {
        active: false,
        startX: 0,
        startScrollLeft: 0,
      },
      categoriesJustDragged: false,
    }
  },
  computed: {
    items() {
      return this.$store.state.items || []
    },
    user() {
      return this.$store.state.user || {}
    },
    isBidDisabled() {
      return this.user.moderation_status === "pending"
    },
    hasFilters() {
      return (
        this.filters.brand.length > 0 ||
        this.filters.color !== null ||
        this.filters.size !== null ||
        this.filters.custom !== null
      )
    },
    isBrandsMode() {
      return this.category === BRANDS_CATEGORY
    },
    isSellersMode() {
      return this.category === SELLERS_CATEGORY
    },
    searchPlaceholder() {
      if (this.isBrandsMode) return "Поиск по брендам"
      if (this.isSellersMode) return "Поиск по селлерам"
      // «Поиск по N товарам» (дательный падеж: по 1 товару / по 2 товарам) —
      // N из /market/catalog total_products (обновляется вместе с выдачей,
      // в т.ч. под фильтрами)
      const n = Number(this.$store.state.marketTotalProducts) || 0
      if (n <= 0) return "Поиск по товарам"
      const word = pluralizeWord(n, {
        one: "товару",
        few: "товарам",
        many: "товарам",
      })
      return `Поиск по ${n} ${word}`
    },
    currentSortOptions() {
      if (this.isSellersMode) {
        return this.sellersSortOptions
      }
      return this.sortOptions
    },
    shouldShowFilters() {
      if (!this.category) return false
      // Скрываем только в режимах брендов и селлеров
      return !this.isSellersMode && !this.isBrandsMode
    },
    shouldShowSort() {
      if (!this.category) return false
      // Скрываем только в режиме брендов
      return !this.isBrandsMode
    },
  },

  beforeRouteLeave() {
    const categoriesEl = this.$refs.categoriesContainer
    this.$store.commit("setMarketViewState", {
      category: this.category,
      q: this.q,
      sort: this.sort,
      filters: { ...this.filters },
      offset: this.offset,
      hasMore: this.hasMore,
      items: [...(this.$store.state.items || [])],
      scrollY: window.pageYOffset || document.documentElement.scrollTop,
      categoriesScrollLeft: categoriesEl ? categoriesEl.scrollLeft : 0,
      brandList: this.isBrandsMode ? [...(this.brandList || [])] : undefined,
      sellersList: this.isSellersMode
        ? [...(this.sellersList || [])]
        : undefined,
      sellersStats: this.isSellersMode
        ? this.sellersStats
          ? { ...this.sellersStats }
          : null
        : undefined,
    })
  },

  async mounted() {
    this.categoriesLoading = true
    const categoriesRaw = (await this.$store.dispatch("getCategories")) || []
    const productCategories = [...categoriesRaw].sort((a, b) => {
      const i = PRODUCT_CATEGORY_ORDER.indexOf(a.name)
      const j = PRODUCT_CATEGORY_ORDER.indexOf(b.name)
      if (i === -1 && j === -1) return 0
      if (i === -1) return 1
      if (j === -1) return -1
      return i - j
    })
    this.categories = [
      { name: CATALOG_CATEGORY, label: "Каталог" },
      { name: BRANDS_CATEGORY, label: "Бренды" },
      ...productCategories,
      { name: SELLERS_CATEGORY, label: "Селлеры" },
    ]
    this.defaultCategoryName = CATALOG_CATEGORY
    this.categoriesLoading = false
    this.$nextTick(() => {
      this.updateScrollButtons()
    })

    const saved = this.$store.state.marketViewState
    if (saved) {
      this.category = saved.category || this.defaultCategoryName
      this.q = saved.q
      this.sort = saved.sort || "random"
      this.filters = { ...this.filters, ...saved.filters }
      this.offset = saved.offset ?? 0
      this.hasMore = saved.hasMore !== false
      this.isLoading = false
      this.isLoadingMore = false
      if (
        Array.isArray(saved.items) &&
        saved.items.length > 0 &&
        !this.isBrandsMode &&
        !this.isSellersMode
      ) {
        this.$store.commit("setItems", saved.items)
      }
      if (saved.brandList && this.category === BRANDS_CATEGORY) {
        this.brandList = saved.brandList
      }
      if (saved.sellersList && this.category === SELLERS_CATEGORY) {
        this.sellersList = saved.sellersList
        this.sellersStats = saved.sellersStats || null
      }
      this.$store.commit("setMarketViewState", null)
      if (this.category === BRANDS_CATEGORY) {
        this.updateRouteTab("brands")
      } else if (this.category === SELLERS_CATEGORY) {
        this.updateRouteTab("sellers")
      } else if (this.$route.query.tab) {
        const q = { ...this.$route.query }
        delete q.tab
        this.$router.replace({ query: q })
      }
      this.$nextTick(() => {
        this.updateScrollButtons()
        if (
          saved.categoriesScrollLeft != null &&
          this.$refs.categoriesContainer
        ) {
          this.$refs.categoriesContainer.scrollLeft = saved.categoriesScrollLeft
        }
        if (
          this.category === BRANDS_CATEGORY ||
          this.category === SELLERS_CATEGORY
        ) {
          this.scrollActiveCategoryIntoView()
        }
        const scrollY = saved.scrollY
        if (typeof scrollY === "number" && scrollY >= 0) {
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollY)
          })
        }
      })
    } else {
      const tab = this.$route.query.tab
      const queryCategory = this.$route.query.category
      if (tab === "brands") {
        this.category = BRANDS_CATEGORY
        await this.loadBrands()
      } else if (tab === "sellers") {
        this.category = SELLERS_CATEGORY
        if (
          !this.sort ||
          (this.sort !== "rating_desc" && this.sort !== "rating_asc")
        ) {
          this.sort = "rating_desc"
        }
        await this.loadSellers()
      } else if (
        queryCategory &&
        this.categories.some((c) => c.name === queryCategory)
      ) {
        this.category = queryCategory
        await this.loadItems(true)
      } else {
        this.category = this.defaultCategoryName
        await this.loadItems(true)
      }
      this.$nextTick(() => {
        this.updateScrollButtons()
        if (
          this.category === BRANDS_CATEGORY ||
          this.category === SELLERS_CATEGORY
        ) {
          this.scrollActiveCategoryIntoView()
        }
      })
    }

    window.addEventListener("resize", this.updateScrollButtons)
    window.addEventListener("scroll", this.handleScroll)
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateScrollButtons)
    window.removeEventListener("scroll", this.handleScroll)
    document.removeEventListener("mousemove", this.onCategoriesMouseMove)
    document.removeEventListener("mouseup", this.onCategoriesMouseUp)
    document.body.style.userSelect = ""
    document.body.style.cursor = ""
  },
  watch: {
    categories() {
      this.$nextTick(() => {
        this.updateScrollButtons()
      })
    },
  },
  methods: {
    pluralizeProducts,
    pluralizeSellers(count) {
      return pluralizeWord(count, {
        one: "Селлер",
        few: "Селлера",
        many: "Селлеров",
      })
    },
    pluralizeResellers(count) {
      return pluralizeWord(count, {
        one: "Реселлер",
        few: "Реселлера",
        many: "Реселлеров",
      })
    },
    updateScrollButtons() {
      const container = this.$refs.categoriesContainer
      if (container) {
        this.canScrollLeft = container.scrollLeft > 0
        this.canScrollRight =
          container.scrollLeft <
          container.scrollWidth - container.clientWidth - 1
      } else {
        this.canScrollLeft = false
        this.canScrollRight = false
      }
    },
    onCategoryClick(categoryName) {
      if (this.categoriesJustDragged) {
        this.categoriesJustDragged = false
        return
      }
      this.selectCategory(categoryName)
    },
    onCategoriesMouseDown(e) {
      const el = this.$refs.categoriesContainer
      if (!el) return
      this.categoriesDrag.active = true
      this.categoriesDrag.startX = e.pageX
      this.categoriesDrag.startScrollLeft = el.scrollLeft
      document.addEventListener("mousemove", this.onCategoriesMouseMove)
      document.addEventListener("mouseup", this.onCategoriesMouseUp)
      document.body.style.userSelect = "none"
      document.body.style.cursor = "grabbing"
    },
    onCategoriesMouseMove(e) {
      if (!this.categoriesDrag.active) return
      const el = this.$refs.categoriesContainer
      if (!el) return
      e.preventDefault()
      const walk = (e.pageX - this.categoriesDrag.startX) * 1.5
      el.scrollLeft = this.categoriesDrag.startScrollLeft - walk
      if (Math.abs(walk) > 5) this.categoriesJustDragged = true
      this.updateScrollButtons()
    },
    onCategoriesMouseUp() {
      this.categoriesDrag.active = false
      document.removeEventListener("mousemove", this.onCategoriesMouseMove)
      document.removeEventListener("mouseup", this.onCategoriesMouseUp)
      document.body.style.userSelect = ""
      document.body.style.cursor = ""
      this.updateScrollButtons()
    },
    async selectCategory(category) {
      if (this.category === category) return
      const wasBrandsOrSellers = this.isBrandsMode || this.isSellersMode
      this.isTransitioning = true
      this.category = category
      this.offset = 0
      try {
        if (this.isBrandsMode) {
          this.updateRouteTab("brands")
          await this.loadBrands()
        } else if (this.isSellersMode) {
          // Устанавливаем сортировку по умолчанию для селлеров
          if (
            !this.sort ||
            (this.sort !== "rating_desc" && this.sort !== "rating_asc")
          ) {
            this.sort = "rating_desc"
          }
          this.updateRouteTab("sellers")
          await this.loadSellers()
        } else {
          // При переходе с брендов/селлеров URL меняется → компонент ремаунтится.
          // Сохраняем категорию в query, чтобы после ремаунта восстановить её.
          if (wasBrandsOrSellers) {
            const query = { ...this.$route.query }
            delete query.tab
            query.category = category
            this.$router.replace({ query })
          } else {
            this.updateRouteTab(null)
            await this.loadItems(true)
          }
        }
      } finally {
        this.$nextTick(() => {
          this.isTransitioning = false
          if (this.isBrandsMode || this.isSellersMode) {
            this.scrollActiveCategoryIntoView()
          }
        })
      }
    },

    scrollActiveCategoryIntoView() {
      this.$nextTick(() => {
        const container = this.$refs.categoriesContainer
        if (!container) return
        const active = container.querySelector(
          `[data-category="${this.category}"]`
        )
        if (active) {
          active.scrollIntoView({
            inline: "center",
            block: "nearest",
            behavior: "smooth",
          })
        }
        this.updateScrollButtons()
      })
    },

    async loadItems(reset = false) {
      if (this.isBrandsMode || this.isSellersMode) return

      if (reset) {
        this.offset = 0
        this.hasMore = true
        this.isLoading = true
      } else {
        if (!this.hasMore || this.isLoadingMore) return
        this.isLoadingMore = true
      }

      const payload = {
        limit: this.limit,
        offset: this.offset,
        sort: this.sort,
        category:
          this.category === CATALOG_CATEGORY ? undefined : this.category,
        q: this.q,
        brand: this.filters.brand.length > 0 ? this.filters.brand : undefined,
        color: this.filters.color,
        size: this.filters.size,
        size_type: this.filters.size_type || undefined,
        is_custom:
          this.filters.custom !== null ? this.filters.custom : undefined,
      }

      try {
        const response = await this.$store.dispatch("getMarketItems", payload)
        const newItems = response?.items || []

        // Проверяем, есть ли еще данные
        this.hasMore = newItems.length === this.limit
        if (this.hasMore) {
          this.offset += this.limit
        }
      } finally {
        this.isLoading = false
        this.isLoadingMore = false
      }
    },
    handleScroll() {
      if (this.isBrandsMode || this.isSellersMode) return
      if (this.isLoading || this.isLoadingMore || !this.hasMore) return

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      // Загружаем, когда осталось 200px до конца
      if (scrollTop + windowHeight >= documentHeight - 200) {
        this.loadItems(false)
      }
    },

    onSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.offset = 0
        if (this.isBrandsMode) {
          this.loadBrands()
        } else if (this.isSellersMode) {
          this.loadSellers()
        } else {
          this.loadItems(true)
        }
      }, 400)
    },

    openSort() {
      this.sortClosing = false
      this.sortModal = true
      // Устанавливаем состояние открытия сортировки в store
      this.$store.commit("setSortModalOpen", true)
    },

    closeSort() {
      this.sortClosing = true
      // Сбрасываем состояние открытия сортировки в store
      this.$store.commit("setSortModalOpen", false)

      setTimeout(() => {
        this.sortModal = false
        this.sortClosing = false
      }, 250)
    },

    async applySort(val) {
      this.sort = val
      this.closeSort()
      if (this.isBrandsMode) {
        await this.loadBrands()
      } else if (this.isSellersMode) {
        await this.loadSellers()
      } else {
        await this.loadItems(true)
      }
    },

    async applyFilters(newFilters) {
      this.filters = newFilters
      this.filtersOpen = false
      this.offset = 0
      if (this.isBrandsMode) {
        await this.loadBrands()
      } else if (this.isSellersMode) {
        await this.loadSellers()
      } else {
        await this.loadItems(true)
      }
    },

    async openModel(id) {
      this.$router.push({
        name: "Model",
        params: { id },
      })
    },
    async loadBrands() {
      this.brandLoading = true
      try {
        const payload = {
          q: this.q || undefined,
          limit: this.brandLimit,
          offset: 0,
        }
        const response = await this.$store.dispatch("getBrands", payload)
        const items = Array.isArray(response?.items)
          ? response.items
          : Array.isArray(response)
          ? response
          : []
        this.brandList = items.map((item, idx) => ({
          name: item.brand || item.name || `Бренд ${idx + 1}`,
          count: item.count ?? item.total ?? 0,
        }))
      } catch (error) {
        console.error("Ошибка при загрузке брендов:", error)
        this.brandList = []
      } finally {
        this.brandLoading = false
      }
    },
    openBrand(brand) {
      this.$router.push({
        name: "Brand",
        params: { brand: brand.name },
        query: {
          q: this.q || undefined,
          color: this.filters.color || undefined,
          size: this.filters.size || undefined,
          size_type: this.filters.size_type || undefined,
          brand: this.filters.brand.join(",") || undefined,
          sort: this.sort,
        },
      })
    },
    async loadSellers() {
      this.sellersLoading = true
      try {
        const payload = {
          q: this.q || undefined,
          limit: this.sellersLimit,
          offset: 0,
          sort: this.sort || "rating_desc", // rating_desc, rating_asc
        }
        const response = await this.$store.dispatch("getSellers", payload)
        const items = Array.isArray(response?.items)
          ? response.items
          : Array.isArray(response)
          ? response
          : []
        this.sellersList = items.map((item) => ({
          id: item.id,
          name: item.login || item.username || "Продавец",
          count: item.products_count || 0,
          avatar: item.photo_url,
          rating: item.rating ? Number(item.rating).toFixed(1) : "0.0",
          rating_count: item.rating_count ?? 0,
        }))
        this.sellersStats = {
          sellers_total: response.sellers_total ?? 0,
          resellers_total: response.resellers_total ?? 0,
        }
      } catch (error) {
        console.error("Ошибка при загрузке селлеров:", error)
        this.sellersList = []
      } finally {
        this.sellersLoading = false
      }
    },
    openSeller(seller) {
      this.$router.push({
        name: "Seller",
        params: { id: seller.id },
      })
    },
    async openBrandsSection() {
      this.filtersOpen = false
      if (!this.isBrandsMode) {
        this.category = BRANDS_CATEGORY
        this.updateRouteTab("brands")
        await this.loadBrands()
      }
    },
    updateRouteTab(tab) {
      const current = this.$route.query.tab
      if (tab === current || (!tab && !current)) return
      const query = { ...this.$route.query }
      if (tab) {
        query.tab = tab
        delete query.category
      } else {
        delete query.tab
      }
      this.$router.replace({ query })
    },
    openBid() {
      if (this.isBidDisabled) {
        return false
      }
      this.$router.push({ name: "Bid" })
    },
  },
}
</script>

<style lang="scss" module>
.wrapper {
  padding: 0.5rem 1rem 6.5rem 1rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

/* HEADER */
.topBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.searchBox {
  flex: 1;
  border: 1px solid #b8b8bc5c;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  margin-right: 0.375rem;

  img {
    width: 1.5rem;
    height: 1.5rem;
  }

  input {
    flex: 1;
    border: none;
    background: none;
    font-size: 0.875rem;
    outline: none;
    max-width: 100%;
    width: 100%;
  }
}

.iconBtn {
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.5rem;
  border: 1px solid #b8b8bc5c;
  background: transparent;
  border-radius: 0.5rem;
  margin-right: 0.375rem;

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  &:last-child {
    margin-right: 0;
  }
}

.filterActive {
  border: 1px solid #000;
}

/* CATEGORIES */
.categoriesWrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.categories {
  display: flex;
  overflow-x: auto;
  flex: 1;
  min-width: 0;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  cursor: grab;
  user-select: none;
}

.categoriesGrabbing {
  cursor: grabbing;
}

.categories::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

/* Белая тень по краям — подсказка, что можно скроллить */
.categoriesFadeLeft {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 1.5rem;
  background: linear-gradient(to right, #fff 0%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
  z-index: 1;
}

.categoriesFadeRight {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 1.5rem;
  background: linear-gradient(to left, #fff 0%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
  z-index: 1;
}

.categoryBtn {
  background: none;
  border: none;
  font-size: 0.875rem;
  opacity: 0.5;
  padding: 1rem 0.5rem;
  color: #000000;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: grab;
}

.active {
  opacity: 1;
  font-weight: 600;
}

.categorySkeleton {
  padding: 1rem 0.75rem;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.categorySkeletonBar {
  width: 60px;
  height: 16px;
  border-radius: 4px;
}

/* GRID */
.grid {
  margin-top: 0.5rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.brandListWrapper {
  margin-top: 0.5rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.brandList {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.brandCard {
  width: 100%;
  max-width: 100%;
  text-align: left;
  border-radius: 0.625rem;
  border: 1px solid #ebeaea;
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  box-sizing: border-box;
  overflow: hidden;
}

.brandName {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.brandCount {
  font-size: 0.875rem;
  color: #7b7b81;
}

.brandChevron {
  display: flex;
  align-items: center;
  margin-left: 0.5rem;
}

.sellerRating {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;
  margin-right: 0.5rem;
}

.ratingValue {
  font-size: 1rem;
  color: #000;
}

.ratingStar {
  width: 1rem;
  height: 1rem;
}

.sellersStats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 1rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.statCard {
  background: #f5f7fa;
  border-radius: 0.625rem;
  padding: 0.625rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
}

.statValue {
  font-size: 1.25rem;
  font-weight: 600;
  color: #000;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.statLabel {
  font-size: 0.875rem;
  color: #8e8e93;
  font-weight: 400;
}

.sellerCardContent {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.sellerAvatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #f0f0f5;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #8e8e93;
  font-size: 1rem;
}

.sellerAvatarPlaceholder {
  background: #e5e5ea;
  color: #000;
}

.sellerAvatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sellerAvatarSkeleton {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 0.75rem;
  max-width: 100%;
  box-sizing: border-box;
}

.sellerInfoSkeleton {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-width: 100%;
  box-sizing: border-box;
}

.brandTitleSkeleton {
  width: 60%;
  max-width: 100%;
  height: 1rem;
  border-radius: 0.5rem;
  box-sizing: border-box;
}

.brandCountSkeleton {
  width: 30%;
  max-width: 100%;
  height: 0.75rem;
  border-radius: 0.5rem;
  box-sizing: border-box;
}

.cardSkeleton {
  padding: 0.75rem;
  border-radius: 0.75rem;
  border: 1px solid #b8b8bc5c;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.skeletonImage {
  width: 100%;
  height: 5.5rem;
  border-radius: 0.5rem;
}

.skeletonLine {
  width: 100%;
  height: 0.75rem;
  border-radius: 0.375rem;
}

.skeletonLineShort {
  width: 60%;
  height: 0.75rem;
  border-radius: 0.375rem;
}

/* ===== SORT MODAL ===== */

/* Open blur */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Close blur */
@keyframes fadeOut {
  from {
    opacity: 1;
    backdrop-filter: blur(10px);
  }
  to {
    opacity: 0;
    backdrop-filter: blur(0px);
  }
}

/* Panel open */
@keyframes slideUp {
  from {
    transform: translateY(40px);
  }
  to {
    transform: translateY(0);
  }
}

/* Panel close */
@keyframes slideDownAnim {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(40px);
    opacity: 0;
  }
}

.sortBg {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 1000;
  animation: fadeIn 0.25s ease;
}

.fadeOut {
  animation: fadeOut 0.25s ease forwards;
}

.sortModal {
  width: 100%;
  background: #fff;
  border-radius: 1.5rem 1.5rem 0 0;
  padding: 1.5rem 1rem;
  animation: slideUp 0.25s ease;
}

.slideDown {
  animation: slideDownAnim 0.25s ease forwards;
}

.sortHeader {
  width: 3.25rem;
  height: 0.375rem;
  background: #ddd;
  border-radius: 0.5rem;
  margin: 0 auto 0.875rem;
}

.sortTitle {
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
}

.sortList {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sortItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f7f7f7;
  border-radius: 0.5rem;
}

.radio {
  position: relative;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 1px solid #bbb;
  img {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

.radioActive {
  background: #f07818;
  border-color: #f07818;
}

.emptyState {
  grid-column: 1 / -1;
  background: #ffffff;
  border-radius: 0.75rem;
  border: 1px dashed #d4d4db;
  padding: 2rem 1.25rem;
  text-align: center;
  color: #6f6f78;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.emptyState p {
  font-size: 1rem;
  font-weight: 600;
  color: #111111;
}

.emptyState span {
  font-size: 0.875rem;
  color: #6f6f78;
}

.loadingMore {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

.loadingSpinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
